'use client';

import type { FC } from 'react';
import { GiOpenChest, GiCrossedSwords, FaAtom, FaGift, FaArrowUp, FaStore, FaTrophy, FaDice, FaGavel, FaUserShield, FaCrosshairs } from '../icons';
import type { IconType } from '../icons';
import {
  UserInventoryItemResponseSourceEnum,
  RecentInventoryItemSourceEnum,
} from '../../services/generated/Api';

type SourceType = UserInventoryItemResponseSourceEnum | RecentInventoryItemSourceEnum | string;

interface DropSourceIconProps {
  source: SourceType;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

/**
 * Maps source types to their corresponding icons
 */
const getIconBySource = (source: SourceType): IconType | null => {
  switch (source) {
    case UserInventoryItemResponseSourceEnum.CASE_OPENING:
    case RecentInventoryItemSourceEnum.CASE_OPENING:
      return GiOpenChest;
    case UserInventoryItemResponseSourceEnum.CASE_BATTLE:
      return GiCrossedSwords;
    case UserInventoryItemResponseSourceEnum.CONTRACT:
      return FaAtom;
    case UserInventoryItemResponseSourceEnum.GIFT:
    case RecentInventoryItemSourceEnum.GIFT:
      return FaGift;
    case UserInventoryItemResponseSourceEnum.UPGRADE:
      return FaArrowUp;
    case UserInventoryItemResponseSourceEnum.AUCTION:
      return FaGavel;
    case UserInventoryItemResponseSourceEnum.FROM_SHOP:
    case RecentInventoryItemSourceEnum.PURCHASE:
      return FaStore;
    case UserInventoryItemResponseSourceEnum.DRAW:
      return FaDice;
    case UserInventoryItemResponseSourceEnum.CONTEST:
      return FaTrophy;
    case UserInventoryItemResponseSourceEnum.ADMIN_GRANT:
      return FaUserShield;
    case UserInventoryItemResponseSourceEnum.RUSH_MID:
      return FaCrosshairs;
    default:
      return null;
  }
};

/**
 * Maps source types to their display colors
 */
const getColorBySource = (source: SourceType): string => {
  switch (source) {
    case UserInventoryItemResponseSourceEnum.CASE_OPENING:
    case RecentInventoryItemSourceEnum.CASE_OPENING:
      return '#f59e0b'; // amber - cases
    case UserInventoryItemResponseSourceEnum.CASE_BATTLE:
      return '#ef4444'; // red - battles
    case UserInventoryItemResponseSourceEnum.CONTRACT:
      return '#8b5cf6'; // purple - contracts
    case UserInventoryItemResponseSourceEnum.GIFT:
    case RecentInventoryItemSourceEnum.GIFT:
      return '#ec4899'; // pink - gifts
    case UserInventoryItemResponseSourceEnum.UPGRADE:
      return '#10b981'; // green - upgrades
    case UserInventoryItemResponseSourceEnum.AUCTION:
      return '#3b82f6'; // blue - auctions
    case UserInventoryItemResponseSourceEnum.FROM_SHOP:
    case RecentInventoryItemSourceEnum.PURCHASE:
      return '#6366f1'; // indigo - shop
    case UserInventoryItemResponseSourceEnum.DRAW:
      return '#f97316'; // orange - draws
    case UserInventoryItemResponseSourceEnum.CONTEST:
      return '#eab308'; // yellow - contests
    case UserInventoryItemResponseSourceEnum.ADMIN_GRANT:
      return '#94a3b8'; // slate - admin
    case UserInventoryItemResponseSourceEnum.RUSH_MID:
      return '#22d3ee'; // cyan - Rush Mid drop
    default:
      return '#ffffff';
  }
};

/**
 * Maps size to font size
 */
const getSizeValue = (size: 'small' | 'medium' | 'large'): number => {
  switch (size) {
    case 'small':
      return 12;
    case 'medium':
      return 16;
    case 'large':
      return 20;
    default:
      return 16;
  }
};

/**
 * Component that displays an icon representing the source of a drop.
 * Supports various source types: CASE_OPENING, CASE_BATTLE, CONTRACT, GIFT, etc.
 */
export const DropSourceIcon: FC<DropSourceIconProps> = ({
  source,
  size = 'medium',
  className,
}) => {
  const Icon = getIconBySource(source);

  if (!Icon) {
    return null;
  }

  const color = getColorBySource(source);
  const fontSize = getSizeValue(size);

  return (
    <span
      className={`inline-flex items-center justify-center leading-none drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)] ${className || ''}`}
      style={{
        color,
        fontSize: `${fontSize}px`,
      }}
    >
      <Icon />
    </span>
  );
};
